import { lazy } from 'react'
import { LoaderFunctionArgs, Navigate, Outlet } from 'react-router-dom'
import loaderCommon from '@/lib/helpers/loaderCommon'
import { getCombinedOrganizationData } from '@/routes/settings/organization/users/actions'
import { getOrganizationStatusGroups } from '@/routes/settings/statuses/actions'
import { getOrganizationLabelGroups } from '@/routes/settings/labels/actions'
import { getProjectRoles } from '@/routes/settings/projectRoles/lib/loader'
import issueTypeLoader from '@/routes/settings/issueTypes/loader.ts'
import { loaderSingleProject } from '@/routes/settings/projectDetails/lib/actions.ts'

const OrganizationUsers = lazy(
  () => import('@/routes/settings/organization/users/view/organizationUsers.tsx'),
)
const OrganizationView = lazy(
  () => import('@/routes/settings/organization/information/view/organization.tsx'),
)
const ProtectedRoute = lazy(() => import('@/components/common/wrappers/ProtectedRoute.tsx'))
const ProjectDetails = lazy(
  () => import('@/routes/settings/projectDetails/view/ProjectDetails.tsx'),
)
const LabelsView = lazy(() => import('@/routes/settings/labels/view/Labels.tsx'))
const Projects = lazy(() => import('@/routes/settings/projects/view/Projects.tsx'))
const ProjectStageDetails = lazy(
  () => import('@/routes/settings/projectDetails/view/ProjectStageDetails.tsx'),
)
const StatusesView = lazy(() => import('@/routes/settings/statuses/view/statuses.tsx'))
const IssueTypes = lazy(() => import('@/routes/settings/issueTypes/view/IssueTypesView.tsx'))
const AccountView = lazy(() => import('@/routes/settings/account/view/account.tsx'))
const OfflineCacheView = lazy(
  () => import('@/routes/settings/offlineCache/view/OfflineCacheView.tsx'),
)
const ProjectRolesView = lazy(
  () => import('@/routes/settings/projectRoles/view/ProjectRolesView.tsx'),
)
const SettingsRoot = lazy(() => import('@/routes/settings/settingsRoot/SettingsRoot.tsx'))

export const SETTINGS_ROUTES = {
  path: 'settings',
  element: <SettingsRoot />,
  children: [
    {
      index: true,
      element: <Navigate to="/settings/account" replace />,
    },
    {
      path: 'organization',
      element: <Outlet />,
      children: [
        {
          path: 'information',
          element: (
            <ProtectedRoute allowedRoles={['ROLE_ORGANIZATION_OWNER', 'ROLE_ORGANIZATION_ADMIN']}>
              <OrganizationView />
            </ProtectedRoute>
          ),
        },
        {
          path: 'users',
          element: (
            <ProtectedRoute allowedRoles={['ROLE_ORGANIZATION_OWNER', 'ROLE_ORGANIZATION_ADMIN']}>
              <OrganizationUsers />
            </ProtectedRoute>
          ),
          loader: () => loaderCommon(getCombinedOrganizationData),
        },
        {
          path: 'projects',
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <Projects />,
            },
            {
              path: ':projectId',
              element: <ProjectDetails />,
              loader: (params: LoaderFunctionArgs) => loaderCommon(loaderSingleProject, params),
              children: [
                {
                  path: 'project_stages/:projectStageId',
                  loader: (params: LoaderFunctionArgs) => loaderCommon(loaderSingleProject, params),
                  element: <ProjectStageDetails />,
                  children: [
                    {
                      path: 'issue_types',
                      element: null,
                    },
                    {
                      path: 'labels',
                      element: null,
                    },
                    {
                      path: 'statuses',
                      element: null,
                    },
                    {
                      path: 'project-roles',
                      element: null,
                      loader: (params: LoaderFunctionArgs) => loaderCommon(getProjectRoles, params),
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },

    {
      path: 'statuses',
      element: (
        <ProtectedRoute allowedRoles={['ROLE_ORGANIZATION_OWNER', 'ROLE_ORGANIZATION_ADMIN']}>
          <StatusesView />
        </ProtectedRoute>
      ),
      loader: () => loaderCommon(getOrganizationStatusGroups),
    },
    {
      path: 'labels',
      element: (
        <ProtectedRoute allowedRoles={['ROLE_ORGANIZATION_OWNER', 'ROLE_ORGANIZATION_ADMIN']}>
          <LabelsView />
        </ProtectedRoute>
      ),
      loader: () => loaderCommon(getOrganizationLabelGroups),
    },
    {
      path: 'issue_types',
      element: (
        <ProtectedRoute allowedRoles={['ROLE_ORGANIZATION_OWNER', 'ROLE_ORGANIZATION_ADMIN']}>
          <IssueTypes />
        </ProtectedRoute>
      ),
      loader: () => loaderCommon(issueTypeLoader),
    },
    {
      path: 'account',
      element: <AccountView />,
    },
    {
      path: 'offline-cache',
      element: <OfflineCacheView />,
    },

    {
      path: 'project-roles',
      element: (
        <ProtectedRoute allowedRoles={['ROLE_ORGANIZATION_OWNER', 'ROLE_ORGANIZATION_ADMIN']}>
          <ProjectRolesView />
        </ProtectedRoute>
      ),
      loader: (params: LoaderFunctionArgs) => loaderCommon(getProjectRoles, params),
    },
  ],
}
